// src/App.tsx
import { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, SocketProvider } from './context';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { NetworkProvider } from './context/NetworkContext';
import { MainLayout, AuthLayout } from './components/layout';
import { DashboardLayout } from './components/layout';
import AdminLayout from './components/admin/layout/AdminLayout';
import { LoadingSpinner } from './components/common';
import CrispChat from './components/common/CrispChat';
import ConnectionLostOverlay from './components/common/ConnectionLostOverlay';
import { useAuth } from './hooks';
import { ROUTES } from './utils/constants';
// Import ScrollToTop component
import ScrollToTop from './components/common/ScrollToTop';
// Import scroll reset utilities
import { configureScrollBehavior, forceScrollToTop } from './utils/scrollReset';
// Import React Query
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Refetch data when the user revisits the tab
      refetchOnWindowFocus: true,
      // Consider data stale after 5 seconds
      staleTime: 5000,
      // Retry failed queries 1 time
      retry: 1,
    },
  },
});

// Lazy load components for code splitting
const Home = lazy(() => import('./pages/Home'));
const UseCases = lazy(() => import('./pages/UseCases'));
const ServiceProviders = lazy(() => import('./pages/ServiceProviders')); 
const Login = lazy(() => import('./pages/auth/Login'));
const Signup = lazy(() => import('./pages/auth/Signup'));
const VerifyOtp = lazy(() => import('./pages/auth/VerifyOtp'));
const Dashboard = lazy(() => import('./pages/dashboard/Dashboard'));
const Profile = lazy(() => import('./pages/profile/Profile'));
const MeetingsPage = lazy(() => import('./pages/meetings/MeetingsPage'));
const JoinMeetingsPage = lazy(() => import('./pages/meetings/JoinMeetingsPage'));
const NotFound = lazy(() => import('./pages/NotFound'));

// Legal pages (lazy loaded)
const TermsOfServicePage = lazy(() => import('./pages/TermsOfServicePage'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicyPage'));

// Placeholder for coming soon pages
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));

// Admin pages (lazy loaded)
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard'));
const AdminMeetingApproval = lazy(() => import('./pages/admin/MeetingApproval'));
const AdminMeetingDetailView = lazy(() => import('./pages/admin/MeetingDetailView'));
const AdminUserManagement = lazy(() => import('./pages/admin/UserManagement'));

// Meeting handlers (lazy loaded)
const MeetingLinkHandler = lazy(() => import('./components/meetings/MeetingLinkHandler'));

// Public Meeting Page (lazy loaded)
const PublicMeetingPage = lazy(() => import('./pages/meetings/PublicMeetingPage'));

// Reports page (lazy loaded)
const ReportsPage = lazy(() => import('./pages/reports/ReportsPage'));

// Meeting Passes Page (lazy loaded)
const MeetingPassesPage = lazy(() => import('./pages/subscription/MeetingPassesPage'));

// Payment Callback Page (lazy loaded)
const PaymentCallback = lazy(() => import('./components/subscription/PaymentCallback'));

// Page loading component
const PageLoading = () => (
  <div className="flex h-screen items-center justify-center">
    <LoadingSpinner size="lg" />
  </div>
);

// Protected route component
const ProtectedRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, isLoading } = useAuth();
  
  if (isLoading) {
    return <PageLoading />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} />;
  }
  
  return children;
};

// Admin-only route component
const AdminRoute = ({ children }: { children: JSX.Element }) => {
  const { isAuthenticated, isLoading, isAdmin } = useAuth();
  
  if (isLoading) {
    return <PageLoading />;
  }
  
  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} />;
  }
  
  if (!isAdmin) {
    return <Navigate to={ROUTES.DASHBOARD} />;
  }
  
  return children;
};

const AppRoutes = () => {
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Main layout routes */}
        <Route path={ROUTES.HOME} element={<MainLayout />}>
          <Route index element={<Home />} />
          {/* Add the UseCases route */}
          <Route path={ROUTES.USE_CASES} element={<UseCases />} />
          {/* Add the new ServiceProviders route */}
          <Route path={ROUTES.SERVICE_PROVIDERS} element={<ServiceProviders />} />
          {/* Legal Pages */}
          <Route path={ROUTES.TERMS_OF_SERVICE} element={<TermsOfServicePage />} />
          <Route path={ROUTES.PRIVACY_POLICY} element={<PrivacyPolicyPage />} />
        </Route>
        
        {/* Auth layout routes */}
        <Route element={<AuthLayout />}>
          <Route path={ROUTES.LOGIN} element={<Login />} />
          <Route path={ROUTES.SIGNUP} element={<Signup />} />
          <Route path={ROUTES.VERIFY_OTP} element={<VerifyOtp />} />
        </Route>
        
        {/* Public Meeting Route - Using DashboardLayout but not protected */}
        <Route path="/p/:meetingId" element={<DashboardLayout />}>
          <Route index element={<PublicMeetingPage />} />
        </Route>
        
        {/* Dashboard layout routes - all protected */}
        <Route element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
          <Route path={ROUTES.PROFILE} element={<Profile />} />
          
          {/* Meetings Pages */}
          <Route path={ROUTES.HOST_MEETING} element={<MeetingsPage />} />
          <Route path={ROUTES.JOIN_MEETING} element={<JoinMeetingsPage />} />
          
          {/* Reports Page */}
          <Route path={ROUTES.REPORTS} element={<ReportsPage />} />
          
          {/* Meeting Passes Page */}
          <Route path={ROUTES.MEETING_PASSES} element={<MeetingPassesPage />} />
          
          {/* Payment Callback Page */}
          <Route path={ROUTES.PAYMENT_CALLBACK} element={<PaymentCallback />} />
          
          {/* Coming soon pages */}
          <Route path={ROUTES.SETTINGS} element={<ComingSoonPage title="Settings" />} />
          <Route path={ROUTES.HELP} element={<ComingSoonPage title="Help & Support" />} />
        </Route>
        
        {/* Meeting link route - protected and standalone */}
        <Route path="/m/:meetingId" element={
          <ProtectedRoute>
            <MeetingLinkHandler />
          </ProtectedRoute>
        } />
        
        {/* Admin layout routes - admin only */}
        <Route element={
          <AdminRoute>
            <AdminLayout />
          </AdminRoute>
        }>
          <Route path={ROUTES.ADMIN_DASHBOARD} element={<AdminDashboard />} />
          <Route path={ROUTES.ADMIN_MEETINGS} element={<AdminMeetingApproval />} />
          <Route path={ROUTES.ADMIN_MEETING_DETAIL.replace(':id', ':id')} element={<AdminMeetingDetailView />} />
          <Route path={ROUTES.ADMIN_USERS} element={<AdminUserManagement />} />
        </Route>
        
        {/* Redirect /admin to /admin/dashboard */}
        <Route path={ROUTES.ADMIN} element={<Navigate to={ROUTES.ADMIN_DASHBOARD} replace />} />
        
        {/* 404 route */}
        <Route path={ROUTES.NOT_FOUND} element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

const App = () => {
  // Configure scroll behavior on app initialization
  useEffect(() => {
    // Set up global scroll behavior override
    configureScrollBehavior();
    
    // Force an initial scroll to top
    forceScrollToTop();
  }, []);
  
  return (
    <BrowserRouter>
      <QueryClientProvider client={queryClient}>
        <NetworkProvider>
          <AuthProvider>
            <SocketProvider>
              <SubscriptionProvider>
                {/* Add CrispChat at the root level to ensure it's available across all routes and refreshes */}
                <CrispChat />
                {/* ScrollToTop must be inside all providers but outside of Routes */}
                <ScrollToTop />
                <ConnectionLostOverlay />
                <AppRoutes />
                <Toaster
                  position="top-right"
                  toastOptions={{
                    style: {
                      background: 'rgba(23, 23, 23, 0.9)',
                      color: '#fff',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    },
                    duration: 5000,
                  }}
                />
              </SubscriptionProvider>
            </SocketProvider>
          </AuthProvider>
        </NetworkProvider>
      </QueryClientProvider>
    </BrowserRouter>
  );
};

export default App;
